import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, message, Tabs, Card, Empty, Space, Tooltip, Statistic, Row, Col } from 'antd';
import {
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  YoutubeOutlined,
  GoogleOutlined,
  PlusOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { verificationAPI } from '../services/api';
import AppLayout from '../components/AppLayout';
import VideoSubmissionModal from '../components/VideoSubmissionModal';
import './MyVideos.css';

const { TabPane } = Tabs;

const MyVideos = () => {
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize
      };

      // Add status filter if not "all"
      if (activeTab !== 'all') {
        params.status = activeTab;
      }

      const response = await verificationAPI.getMyVideos(params);

      if (response.status === 'success') {
        setVideos(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total
        }));
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Gagal memuat daftar video');
    } finally {
      setLoading(false);
    }
  }, [activeTab, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
  };

  const handleVideoSubmitSuccess = () => {
    setVideoModalVisible(false);
    message.success('Video berhasil didaftarkan! Menunggu verifikasi.');
    loadVideos();
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube':
        return <YoutubeOutlined style={{ color: '#ff0000' }} />;
      case 'google':
        return <GoogleOutlined style={{ color: '#4285f4' }} />;
      default:
        return <VideoCameraOutlined />;
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Menunggu Verifikasi' },
      approved: { color: 'green', icon: <CheckCircleOutlined />, text: 'Disetujui' },
      rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'Ditolak' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Calculate stats
  const totalVideos = videos.length;
  const approvedVideos = videos.filter(v => v.verification_status === 'approved').length;
  const pendingVideos = videos.filter(v => v.verification_status === 'pending').length;
  const totalPoints = videos.reduce((sum, v) => sum + (v.verification_score || 0), 0);

  const columns = [
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      render: (platform) => (
        <Space>
          {getPlatformIcon(platform)}
          <span style={{ textTransform: 'capitalize' }}>{platform}</span>
        </Space>
      )
    },
    {
      title: 'Judul',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <a href={record.video_url} target="_blank" rel="noopener noreferrer">
          {title || 'No Title'}
        </a>
      )
    },
    {
      title: 'Hashtag',
      dataIndex: 'hashtag',
      key: 'hashtag',
      width: 120,
      render: (hashtag) => <Tag color="blue">{hashtag}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'verification_status',
      key: 'verification_status',
      width: 180,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Poin',
      dataIndex: 'verification_score',
      key: 'verification_score',
      width: 80,
      align: 'center',
      render: (score, record) => (
        record.verification_status === 'approved' ? (
          <Tag color="green">{score || 0}</Tag>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      )
    },
    {
      title: 'Dikirim',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 170,
      render: (date) => date ? new Date(date).toLocaleString('id-ID') : '-'
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Lihat Video">
            <Button
              type="link"
              icon={<EyeOutlined />}
              href={record.video_url}
              target="_blank"
              rel="noopener noreferrer"
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <AppLayout activeMenu="/my-videos">
      <div className="my-videos-page">
        {/* Stats Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Video"
                value={totalVideos}
                prefix={<VideoCameraOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Disetujui"
                value={approvedVideos}
                valueStyle={{ color: '#14B8A6' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Menunggu"
                value={pendingVideos}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Poin"
                value={totalPoints}
                valueStyle={{ color: '#14B8A6' }}
                prefix={<StarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card
          title={
            <Space>
              <VideoCameraOutlined />
              <span>Daftar Video Saya</span>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setVideoModalVisible(true)}
            >
              Submit Video Baru
            </Button>
          }
        >
          <Tabs activeKey={activeTab} onChange={handleTabChange} className="videos-tabs">
            <TabPane
              tab={<span><VideoCameraOutlined /> Semua</span>}
              key="all"
            />
            <TabPane
              tab={<span><ClockCircleOutlined /> Menunggu Verifikasi</span>}
              key="pending"
            />
            <TabPane
              tab={<span><CheckCircleOutlined /> Disetujui</span>}
              key="approved"
            />
            <TabPane
              tab={<span><CloseCircleOutlined /> Ditolak</span>}
              key="rejected"
            />
          </Tabs>

          <Table
            columns={columns}
            dataSource={videos}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} video`,
              pageSizeOptions: ['10', '20', '50']
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Belum ada video yang dikirimkan"
                >
                  <Button type="primary" onClick={() => setVideoModalVisible(true)}>
                    Submit Video Pertama
                  </Button>
                </Empty>
              )
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {record.verification_status === 'rejected' && record.rejection_reason && (
                      <div>
                        <strong>Alasan Ditolak:</strong>
                        <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
                          {record.rejection_reason}
                        </div>
                      </div>
                    )}
                    {record.affiliates && record.affiliates.length > 0 && (
                      <div>
                        <strong>Affiliates:</strong>
                        <div style={{ marginTop: '8px' }}>
                          {record.affiliates.map((aff, idx) => (
                            <Tag key={idx} color="blue" style={{ marginTop: '4px' }}>
                              {aff.name} ({aff.type})
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                    {record.verified_by && (
                      <div>
                        <strong>Diverifikasi oleh:</strong> {record.verified_by}
                        {record.verified_at && (
                          <span style={{ marginLeft: '16px', color: '#666' }}>
                            pada {new Date(record.verified_at).toLocaleString('id-ID')}
                          </span>
                        )}
                      </div>
                    )}
                  </Space>
                </div>
              ),
              rowExpandable: (record) => (
                record.verification_status !== 'pending' ||
                (record.affiliates && record.affiliates.length > 0)
              )
            }}
          />
        </Card>

        {/* Video Submission Modal */}
        <VideoSubmissionModal
          visible={videoModalVisible}
          onClose={() => setVideoModalVisible(false)}
          onSuccess={handleVideoSubmitSuccess}
        />
      </div>
    </AppLayout>
  );
};

export default MyVideos;
